const ERROR_MONITORING_PATH = "/api/v1/error-monitoring/client-error/";
const MAX_PREVIEW_LENGTH = 2000;

let isInstalled = false;
let originalFetch = null;
const monitoredAxiosInstances = new WeakSet();
let defaultAxiosInstance = null;

const isMonitoringUrl = url => String(url || "").includes(ERROR_MONITORING_PATH);

const safeJsonParse = value => {
  try {
    return JSON.parse(value);
  } catch {
    return null;
  }
};

const getAuthHeader = () => {
  const axiosAuth = defaultAxiosInstance?.defaults?.headers?.common?.Authorization;
  if (axiosAuth) return axiosAuth;

  const userInfo = safeJsonParse(sessionStorage.getItem("user-info"));
  const token = userInfo?.token || userInfo?.access_token || sessionStorage.getItem("token");
  return token ? `Bearer ${token}` : undefined;
};

const getAbsoluteApiUrl = url => {
  if (!url) return "";
  if (/^https?:\/\//i.test(url)) return url;

  const baseUrl = process.env.REACT_APP_API_BASE_URL || window.location.origin;
  try {
    return new URL(url, baseUrl).href;
  } catch {
    return String(url);
  }
};

function redactSensitiveData(value) {
  const sensitiveKeys = ["password", "token", "access", "refresh", "authorization", "secret", "otp"];

  if (Array.isArray(value)) {
    return value.map(item => redactSensitiveData(item));
  }

  if (!value || typeof value !== "object") {
    return value;
  }

  return Object.entries(value).reduce((acc, [key, itemValue]) => {
    const normalizedKey = key.toLowerCase();
    acc[key] = sensitiveKeys.some(sensitiveKey => normalizedKey.includes(sensitiveKey))
      ? "[redacted]"
      : redactSensitiveData(itemValue);
    return acc;
  }, {});
}

const safePreview = value => {
  if (value == null) return undefined;

  try {
    const parsedValue = typeof value === "string" ? safeJsonParse(value) : value;
    const previewValue = parsedValue || value;
    const text = typeof previewValue === "string"
      ? previewValue
      : JSON.stringify(redactSensitiveData(previewValue));
    if (!text) return undefined;
    return text.length > MAX_PREVIEW_LENGTH
      ? `${text.slice(0, MAX_PREVIEW_LENGTH)}...`
      : text;
  } catch {
    return "[unserializable]";
  }
};

const getErrorMessage = error => {
  if (!error) return "Unknown frontend error";
  if (typeof error === "string") return error;
  return error.message || error.reason?.message || String(error);
};

const getErrorStack = error => {
  if (!error) return undefined;
  return error.stack || error.reason?.stack;
};

export const reportFrontendError = async ({
  error,
  message,
  component,
  stack,
  details,
} = {}) => {
  const resolvedDetails = details || {};
  const apiUrl = resolvedDetails.api_url || resolvedDetails.url;

  if (isMonitoringUrl(apiUrl)) return;

  const payload = {
    error_type: "FrontendError",
    message: message || getErrorMessage(error),
    page_url: window.location.href,
    component: component || "frontend",
    stack: stack || getErrorStack(error) || "",
    details: resolvedDetails,
  };

  try {
    const headers = {
      "Content-Type": "application/json",
      "X-Skip-Error-Monitoring": "true",
    };
    const authHeader = getAuthHeader();
    if (authHeader) headers.Authorization = authHeader;

    await (originalFetch || window.fetch)(getAbsoluteApiUrl(ERROR_MONITORING_PATH), {
      method: "POST",
      headers,
      body: JSON.stringify(payload),
    });
  } catch {
    // Error reporting must never block or trigger another report.
  }
};

const normalizeAxiosUrl = config => {
  const baseURL = config?.baseURL || defaultAxiosInstance?.defaults?.baseURL || process.env.REACT_APP_API_BASE_URL || "";
  const url = config?.url || "";

  try {
    return new URL(url, baseURL || window.location.origin).href;
  } catch {
    return `${baseURL}${url}`;
  }
};

export const reportAxiosError = error => {
  const apiUrl = normalizeAxiosUrl(error?.config);
  if (isMonitoringUrl(apiUrl)) return;

  reportFrontendError({
    error,
    component: error?.config?.component || "axios",
    details: {
      api_url: apiUrl,
      http_status: error?.response?.status,
      method: String(error?.config?.method || "").toUpperCase(),
      request_payload: safePreview(error?.config?.data),
      response_body: safePreview(error?.response?.data),
    },
  });
};

const getFetchUrl = input => {
  if (typeof input === "string") return input;
  return input?.url || "";
};

const getFetchMethod = (input, init) => {
  return String(init?.method || input?.method || "GET").toUpperCase();
};

const getFetchPayload = init => {
  if (!init?.body) return undefined;
  if (typeof init.body === "string") return init.body;
  if (typeof FormData !== "undefined" && init.body instanceof FormData) return "[FormData]";
  if (typeof URLSearchParams !== "undefined" && init.body instanceof URLSearchParams) return init.body.toString();
  return "[request body]";
};

const hasSkipMonitoringHeader = headers => {
  if (!headers) return false;
  if (typeof headers.get === "function") {
    return Boolean(headers.get("X-Skip-Error-Monitoring"));
  }
  return Boolean(headers["X-Skip-Error-Monitoring"] || headers["x-skip-error-monitoring"]);
};

const readResponsePreview = async response => {
  try {
    const contentType = response.headers?.get?.("content-type") || "";
    const clonedResponse = response.clone();
    if (contentType.includes("application/json")) {
      return safePreview(await clonedResponse.json());
    }
    return safePreview(await clonedResponse.text());
  } catch {
    return undefined;
  }
};

const installFetchInterceptor = () => {
  if (!window.fetch || originalFetch) return;

  originalFetch = window.fetch.bind(window);
  window.fetch = async (input, init = {}) => {
    const url = getFetchUrl(input);
    const shouldSkip = isMonitoringUrl(url) || hasSkipMonitoringHeader(init?.headers);

    try {
      const response = await originalFetch(input, init);
      if (!shouldSkip && !response.ok) {
        reportFrontendError({
          message: `Fetch request failed with status ${response.status}`,
          component: "fetch",
          details: {
            api_url: getAbsoluteApiUrl(url),
            http_status: response.status,
            method: getFetchMethod(input, init),
            request_payload: safePreview(getFetchPayload(init)),
            response_body: await readResponsePreview(response),
          },
        });
      }
      return response;
    } catch (error) {
      if (!shouldSkip) {
        reportFrontendError({
          error,
          component: "fetch",
          details: {
            api_url: getAbsoluteApiUrl(url),
            method: getFetchMethod(input, init),
            request_payload: safePreview(getFetchPayload(init)),
          },
        });
      }
      throw error;
    }
  };
};

export const attachAxiosErrorMonitoring = axiosInstance => {
  if (!axiosInstance?.interceptors?.response || monitoredAxiosInstances.has(axiosInstance)) return;

  monitoredAxiosInstances.add(axiosInstance);
  axiosInstance.interceptors.response.use(
    response => response,
    error => {
      reportAxiosError(error);
      return Promise.reject(error);
    }
  );
};

export const installFrontendErrorMonitoring = axiosInstance => {
  if (isInstalled) return;
  isInstalled = true;
  defaultAxiosInstance = axiosInstance || null;

  window.onerror = (message, source, lineno, colno, error) => {
    reportFrontendError({
      error,
      message: typeof message === "string" ? message : getErrorMessage(error),
      component: source || "window.onerror",
      details: { source, lineno, colno },
    });
  };

  window.onunhandledrejection = event => {
    reportFrontendError({
      error: event.reason,
      component: "window.onunhandledrejection",
    });
  };

  attachAxiosErrorMonitoring(axiosInstance);

  installFetchInterceptor();
};
